import { useState } from 'react';

/**
 * Keyboard navigation for a combobox popup (W3C APG "combobox with listbox
 * popup"), shared by Combobox and the admin header search.
 *
 *  - ArrowDown opens a closed list and highlights the first enabled row; when open
 *    it moves down. ArrowUp moves up. Disabled rows are skipped.
 *  - Enter picks the highlighted row ONLY. With nothing highlighted it is left
 *    alone, so a surrounding form can still submit.
 *  - Escape closes an open list (and is consumed); with the list closed it is left
 *    alone so a surrounding picker can use it.
 *
 * @param {object}   o
 * @param {number}   o.count        rows in the list
 * @param {boolean}  o.open         the popup is showing
 * @param {(open: boolean) => void} o.setOpen
 * @param {(index: number) => void} o.onPick   called with the highlighted index
 * @param {(index: number) => boolean} [o.isDisabled]  rows that cannot be picked
 */
export default function useListboxNav({ count, open, setOpen, onPick, isDisabled = () => false }) {
  const [highlight, setHighlight] = useState(-1);

  // The next enabled row from `from` in direction `dir` (+1 / -1), or `from`
  // itself when there is none.
  const step = (from, dir) => {
    for (let i = from + dir; i >= 0 && i < count; i += dir) {
      if (!isDisabled(i)) return i;
    }
    return from;
  };

  const reset = () => setHighlight(-1);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (count === 0) return;
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(step(-1, 1));
        return;
      }
      setHighlight((h) => step(h, 1));
    } else if (e.key === 'ArrowUp') {
      if (!open) return;
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? h : step(h, -1)));
    } else if (e.key === 'Enter') {
      if (open && highlight >= 0 && !isDisabled(highlight)) {
        e.preventDefault();
        onPick(highlight);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setHighlight(-1);
      }
    }
  };

  return { highlight, setHighlight, reset, onKeyDown };
}
